import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Star, Building2, Users, Weight, Calendar, Award, TrendingUp, Shield, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { products, services, testimonials, stats, partners } from '../mock/mockData';
import usePageTitle from '../hooks/usePageTitle';
import * as LucideIcons from 'lucide-react';

const Home = () => {
  usePageTitle('');
  // Featured products now include Round Bars
  const featuredProducts = products.slice(0, 3);
  
  // Background image slider
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  
  const backgroundImages = [
    'https://images.unsplash.com/photo-1508465818649-14a170138405?w=1920&h=1080&q=85&fit=crop', // Steel welding with dramatic sparks
    'https://images.unsplash.com/photo-1740328430912-1d2dee9be186?w=1920&h=1080&q=85&fit=crop', // High-rise steel structure building
    'https://images.pexels.com/photos/46167/iron-rods-reinforcing-bars-rods-steel-bars-46167.jpeg?w=1920&h=1080&q=80&fit=crop', // TMT bars
    'https://images.pexels.com/photos/53176/architecture-iron-steel-building-53176.jpeg?w=1920&h=1080&q=80&fit=crop', // Steel framework
    'https://images.unsplash.com/photo-1587293852726-70cdb56c2866?w=1920&h=1080&q=80&fit=crop', // Steel coils
    'https://images.pexels.com/photos/32845680/pexels-photo-32845680.jpeg?w=1920&h=1080&q=80&fit=crop', // Round bars
    'https://images.unsplash.com/photo-1649706286480-1c6f3f3c1fb1?w=1920&h=1080&q=80&fit=crop', // Steel pipes
    'https://images.unsplash.com/photo-1609348632802-b952f368fc3a?w=1920&h=1080&q=85&fit=crop' // Industrial welding with blue sparks
  ];

  // Preload images
  useEffect(() => {
    const imagePromises = backgroundImages.map(src => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.src = src;
        img.onload = resolve;
        img.onerror = reject;
      });
    });

    Promise.all(imagePromises)
      .then(() => setImagesLoaded(true))
      .catch(err => {
        console.log('Some images failed to load, continuing anyway');
        setImagesLoaded(true);
      });
  }, []);

  // Auto-rotate slideshow
  useEffect(() => {
    if (!imagesLoaded) return;
    
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === backgroundImages.length - 1 ? 0 : prevIndex + 1
      );
    }, 4000);

    return () => clearInterval(interval);
  }, [imagesLoaded, backgroundImages.length]);
  
  // Animated counter hook
  const useCounter = (end, duration = 2000) => {
    const [count, setCount] = useState(0);
    useEffect(() => {
      let start = 0;
      const increment = end / (duration / 16);
      const timer = setInterval(() => {
        start += increment;
        if (start >= end) {
          setCount(end);
          clearInterval(timer);
        } else {
          setCount(Math.floor(start));
        }
      }, 16);
      return () => clearInterval(timer);
    }, [end, duration]);
    return count;
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section - Full Width with Slider */}
      <section className="relative min-h-screen flex items-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 overflow-hidden">
        {/* Background Image Slider with Overlay */}
        <div className="absolute inset-0 z-0">
          {backgroundImages.map((image, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-1500 ease-in-out ${
                index === currentImageIndex ? 'opacity-40' : 'opacity-0'
              }`}
              style={{ pointerEvents: 'none' }}
            >
              <img
                src={image}
                alt={`Steel Industry ${index + 1}`}
                className="w-full h-full object-cover"
                loading={index === 0 ? 'eager' : 'lazy'}
              />
            </div>
          ))}
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 via-slate-900/80 to-slate-900/60 z-10"></div>
        </div>

        {/* Animated Background Elements */}
        <div className="absolute inset-0 z-10">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-orange-600/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl animate-pulse delay-700"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-20 py-32">
          <div className="max-w-5xl">
            <div className="inline-block px-6 py-3 bg-orange-600/20 backdrop-blur-sm border border-orange-500/30 text-orange-400 rounded-full text-sm font-semibold mb-8 animate-fade-in">
              <TrendingUp className="inline w-4 h-4 mr-2" />
              Trusted Steel Partner Since 2007
            </div>
            
            <h1 className="text-6xl lg:text-7xl xl:text-8xl font-bold text-white leading-tight mb-8 animate-fade-in-up">
              Your Trusted <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 via-orange-500 to-orange-600">Steel Partner</span>
            </h1>
            
            <p className="text-2xl lg:text-3xl text-slate-300 leading-relaxed mb-12 max-w-3xl animate-fade-in-up delay-200">
              Premium TMT Bars, Round Bars & Structural Steel powering India's infrastructure
            </p>

            <div className="flex flex-wrap gap-6 mb-16 animate-fade-in-up delay-300">
              <Link to="/products">
                <Button className="bg-orange-600 hover:bg-orange-700 text-white px-10 py-8 text-xl rounded-xl shadow-2xl shadow-orange-600/50 hover:shadow-orange-600/70 transition-all hover:scale-105">
                  Explore Products <ArrowRight className="ml-3 w-6 h-6" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="border-2 border-white/30 backdrop-blur-sm text-white hover:bg-white hover:text-slate-900 px-10 py-8 text-xl rounded-xl transition-all hover:scale-105">
                  Request Quote
                </Button>
              </Link>
            </div>

            <div className="flex flex-wrap gap-8 pt-4 animate-fade-in-up delay-400">
              <div className="flex items-center gap-3 text-white/90">
                <div className="w-12 h-12 bg-green-600/20 backdrop-blur-sm border border-green-500/30 rounded-full flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6 text-green-400" />
                </div>
                <span className="text-lg font-semibold">ISO Certified</span>
              </div>
              <div className="flex items-center gap-3 text-white/90">
                <div className="w-12 h-12 bg-blue-600/20 backdrop-blur-sm border border-blue-500/30 rounded-full flex items-center justify-center">
                  <Shield className="w-6 h-6 text-blue-400" />
                </div>
                <span className="text-lg font-semibold">Quality Assured</span>
              </div>
              <div className="flex items-center gap-3 text-white/90">
                <div className="w-12 h-12 bg-purple-600/20 backdrop-blur-sm border border-purple-500/30 rounded-full flex items-center justify-center">
                  <Zap className="w-6 h-6 text-purple-400" />
                </div>
                <span className="text-lg font-semibold">Fast Delivery</span>
              </div>
            </div>
          </div>
        </div>

        {/* Image Slider Indicators */}
        <div className="absolute bottom-20 left-1/2 transform -translate-x-1/2 z-30 flex gap-2">
          {backgroundImages.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentImageIndex(index)}
              className={`h-1 rounded-full transition-all duration-300 ${
                index === currentImageIndex 
                  ? 'bg-orange-500 w-16' 
                  : 'bg-white/40 hover:bg-white/60 w-12'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-30 animate-bounce">
          <div className="w-8 h-12 border-2 border-white/30 rounded-full flex justify-center pt-2">
            <div className="w-1 h-3 bg-white/50 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* Stats Section - Animated Counters */}
      <section className="py-20 bg-white relative">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { end: 18, suffix: '+', label: 'Years Legacy', icon: Calendar, color: 'orange' },
              { end: 500, suffix: '+', label: 'Happy Clients', icon: Users, color: 'blue' },
              { end: 1000, suffix: '+', label: 'Projects', icon: Building2, color: 'green' },
              { end: 50, suffix: 'K+', label: 'Tonnes Supplied', icon: Weight, color: 'purple' }
            ].map((stat, index) => {
              const IconComponent = stat.icon;
              const Counter = () => {
                const count = useCounter(stat.end);
                return <span>{count}{stat.suffix}</span>;
              };
              
              return (
                <div key={index} className="text-center group cursor-pointer">
                  <div className="flex justify-center mb-6">
                    <div className={`w-20 h-20 bg-${stat.color}-100 group-hover:bg-${stat.color}-600 rounded-2xl flex items-center justify-center transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-300 shadow-lg`}>
                      <IconComponent className={`w-10 h-10 text-${stat.color}-600 group-hover:text-white transition-colors`} />
                    </div>
                  </div>
                  <div className="text-5xl font-bold text-slate-900 mb-3"><Counter /></div>
                  <div className="text-slate-600 font-semibold text-lg">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Featured Products</h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Explore our wide range of premium steel products for construction and industrial applications
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {featuredProducts.map((product) => (
              <Card key={product.id} className="group hover:shadow-xl transition-all duration-300 border-slate-200 overflow-hidden">
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 right-4 bg-orange-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                    {product.category}
                  </div>
                </div>
                <CardContent className="p-6">
                  <h3 className="text-2xl font-bold text-slate-900 mb-3">{product.name}</h3>
                  <p className="text-slate-600 mb-4 line-clamp-2">{product.description}</p>
                  <Link to="/products">
                    <Button variant="outline" className="w-full border-slate-300 hover:border-orange-600 hover:text-orange-600">
                      View Details <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="text-center">
            <Link to="/products">
              <Button className="bg-orange-600 hover:bg-orange-700 text-white px-8 py-6 text-lg">
                View All Products <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Our Services</h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Comprehensive steel supply solutions tailored to your project needs
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => {
              const IconComponent = LucideIcons[service.icon];
              return (
                <Card key={service.id} className="border-slate-200 hover:shadow-lg transition-all duration-300 bg-white">
                  <CardContent className="p-8">
                    <div className="w-14 h-14 bg-orange-100 rounded-lg flex items-center justify-center mb-6">
                      <IconComponent className="w-7 h-7 text-orange-600" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-3">{service.title}</h3>
                    <p className="text-slate-600 leading-relaxed">{service.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">What Our Clients Say</h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Trusted by leading construction companies and manufacturers across India
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="border-slate-200 bg-slate-50">
                <CardContent className="p-8">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-orange-500 text-orange-500" />
                    ))}
                  </div>
                  <p className="text-slate-700 mb-6 leading-relaxed italic">"{testimonial.content}"</p>
                  <div className="border-t border-slate-200 pt-4">
                    <div className="font-bold text-slate-900">{testimonial.name}</div>
                    <div className="text-sm text-slate-600">{testimonial.role}</div>
                    <div className="text-sm text-orange-600 font-medium">{testimonial.company}</div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Our Trusted Partners</h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Authorized dealers and suppliers of India's leading steel manufacturers
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8 items-center">
            {partners.map((partner) => (
              <div key={partner.id} className="group">
                <Card className="border-2 border-slate-200 hover:border-orange-600 hover:shadow-xl transition-all duration-300 h-full">
                  <CardContent className="p-8 flex flex-col items-center justify-center text-center h-full">
                    <div className="w-full h-24 flex items-center justify-center mb-4">
                      <div className="text-center">
                        <h3 className="text-2xl font-bold text-slate-800 group-hover:text-orange-600 transition-colors mb-1">
                          {partner.name}
                        </h3>
                        <div className="w-16 h-1 bg-orange-600 mx-auto rounded-full"></div>
                      </div>
                    </div>
                    <p className="text-xs text-slate-600 font-semibold mt-2 group-hover:text-orange-600 transition-colors">
                      {partner.type}
                    </p>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-slate-800 to-slate-900">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Ready to Start Your Project?</h2>
          <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
            Get in touch with our team for expert consultation and competitive quotes
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact">
              <Button className="bg-orange-600 hover:bg-orange-700 text-white px-8 py-6 text-lg">
                Request Quote <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href="tel:+919007396826">
              <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-slate-900 px-8 py-6 text-lg">
                Call Us Now
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
